import { Card, CardContent, Box, Typography, LinearProgress, Chip, Tooltip } from '@mui/material'
import { Lock, CheckCircle } from '@mui/icons-material'
import { motion } from 'framer-motion'
import type { AchievementProgress } from '@/types/achievements'
import { TIER_GRADIENTS, CATEGORY_COLORS } from '@/types/achievements'
import { getAchievementProgressPercentage } from '@/lib/achievements'
import { formatDistanceToNow } from 'date-fns'

interface BadgeCardProps {
  achievement: AchievementProgress
}

export default function BadgeCard({ achievement }: BadgeCardProps) {
  const progressPercentage = getAchievementProgressPercentage(
    achievement.current_progress,
    achievement.target_value
  )

  const isUnlocked = achievement.unlocked

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: isUnlocked ? 1.05 : 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: isUnlocked
            ? TIER_GRADIENTS[achievement.tier]
            : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 100%)',
          border: isUnlocked ? '2px solid' : '1px solid',
          borderColor: isUnlocked ? 'transparent' : 'divider',
          opacity: isUnlocked ? 1 : 0.7,
        }}
      >
        {isUnlocked && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'success.main',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircle sx={{ color: 'white', fontSize: 16 }} />
          </Box>
        )}

        {!isUnlocked && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.3)',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Lock sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 16 }} />
          </Box>
        )}

        <CardContent sx={{ textAlign: 'center', color: isUnlocked ? 'white' : 'text.primary' }}>
          <Box
            sx={{
              fontSize: 48,
              mb: 1,
              filter: isUnlocked ? 'none' : 'grayscale(1)',
              opacity: isUnlocked ? 1 : 0.5,
            }}
          >
            {achievement.icon}
          </Box>

          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              textShadow: isUnlocked ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
            }}
          >
            {achievement.display_name}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontSize: '0.75rem',
              opacity: isUnlocked ? 0.95 : 0.7,
            }}
          >
            {achievement.description}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2 }}>
            <Chip
              label={achievement.tier}
              size="small"
              sx={{
                bgcolor: isUnlocked ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                color: isUnlocked ? 'white' : 'text.secondary',
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            />
            <Chip
              label={`${achievement.points} pts`}
              size="small"
              sx={{
                bgcolor: isUnlocked ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                color: isUnlocked ? 'white' : 'text.secondary',
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            />
          </Box>

          {!isUnlocked && (
            <>
              <Box sx={{ mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: CATEGORY_COLORS[achievement.category],
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {achievement.current_progress} / {achievement.target_value}
              </Typography>
            </>
          )}

          {isUnlocked && achievement.unlocked_at && (
            <Tooltip title={new Date(achievement.unlocked_at).toLocaleDateString()}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  opacity: 0.9,
                  fontSize: '0.65rem',
                }}
              >
                Unlocked {formatDistanceToNow(new Date(achievement.unlocked_at), { addSuffix: true })}
              </Typography>
            </Tooltip>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}
